import { useState } from "react";
import { AlertTriangle, Clock3, House, PawPrint, Plus, Search, Settings2 } from "lucide-react";
import { Button } from "@fawkes/ui";

import type { PensionDier } from "../pensionKlantTypes";
import { datumMetJaar, leeftijd, type Context } from "./hulp";
import { VolVinkje } from "./Tegels";
import type { DieroverzichtVoorkeur } from "./tegelDefinities";

/**
 * De dieren van de klant, links op het tabblad Dieren. Eén klik kiest een dier; rechts staat dan
 * alles over dat dier. Een kaartje laat genoeg zien om het juiste dier te pakken zonder erop te
 * klikken.
 *
 * <p>Wát er op een kaartje staat kiest de gebruiker zelf met het tandwiel: standaard foto, soort, ras,
 * geslacht, leeftijd en de status, maar wie op chipnummers werkt zet die erbij en gooit de rest eruit.
 * De keuze wordt per gebruiker bewaard, naast die van de tegels.
 */
export function DierenLijst({
  dieren,
  gekozen,
  fotos,
  laden,
  fout,
  onOpnieuw,
  onKies,
  onNieuwDier,
  voorkeur,
  onVelden,
  ctx,
}: {
  dieren: PensionDier[];
  gekozen: number | null;
  fotos: Record<string, string>;
  laden: boolean;
  fout: boolean;
  onOpnieuw: () => void;
  onKies: (dierId: number) => void;
  onNieuwDier: () => void;
  voorkeur: DieroverzichtVoorkeur;
  /** Opent het venster waarin de gebruiker de velden van een kaartje kiest. */
  onVelden: () => void;
  ctx: Context;
}) {
  const { t } = ctx;
  const [zoek, setZoek] = useState("");
  const velden = voorkeur.lijst;
  const naald = zoek.trim().toLowerCase();
  const zichtbaar = naald
    ? dieren.filter((d) => [d.naam, d.ras, d.diersoort].some((v) => v?.toLowerCase().includes(naald)))
    : dieren;

  return (
    <aside className="pk-paneel pk-lijst" aria-label={t("pensionKlant.dieren.lijstAria")}>
      <div className="pk-lijst__kop">
        <h2>{t("pensionKlant.dieren.titelAantal", { args: [dieren.length] })}</h2>
        <span className="pk-lijst__knoppen">
          <button
            type="button"
            className="pk-lijst__tandwiel"
            aria-label={t("pensionKlant.dieren.velden")}
            title={t("pensionKlant.dieren.velden")}
            onClick={onVelden}
          >
            <Settings2 size={16} aria-hidden="true" />
          </button>
          <Button variant="primary" onClick={onNieuwDier}>
            <Plus size={17} strokeWidth={2.2} aria-hidden="true" /> {t("pensionKlant.dieren.nieuwDier")}
          </Button>
        </span>
      </div>
      <label className="pk-zoek">
        <Search size={15} aria-hidden="true" />
        <input
          type="search"
          autoComplete="off"
          value={zoek}
          placeholder={t("pensionKlant.dieren.zoek")}
          aria-label={t("pensionKlant.dieren.zoek")}
          onChange={(e) => setZoek(e.target.value)}
        />
      </label>

      {laden ? (
        <p className="pk-lijst__leeg">{t("common.loading")}</p>
      ) : fout ? (
        <div className="pk-lijst__leeg" role="alert">
          <AlertTriangle size={24} aria-hidden="true" />
          <p className="fawkes-error">{t("pensionKlant.laadFout")}</p>
          <Button variant="secondary" onClick={onOpnieuw}>
            {t("common.retry")}
          </Button>
        </div>
      ) : zichtbaar.length === 0 ? (
        <p className="pk-lijst__leeg">
          <PawPrint size={24} aria-hidden="true" />
          {dieren.length === 0 ? t("pensionKlant.dieren.geen") : t("pensionKlant.dieren.geenTreffer")}
        </p>
      ) : (
        <ul className="pk-lijst__dieren">
          {zichtbaar.map((d) => {
            const foto = fotos[String(d.id)];
            return (
              <li key={d.id}>
                <button
                  type="button"
                  className="pk-dierkaart"
                  aria-pressed={gekozen === d.id}
                  onClick={() => onKies(d.id)}
                >
                  {velden.includes("foto") &&
                    (foto ? (
                      <img className="pk-dierkaart__foto" src={foto} alt="" />
                    ) : (
                      <span className="pk-dierkaart__foto" aria-hidden="true">
                        <PawPrint size={24} />
                      </span>
                    ))}
                  <span className="pk-dierkaart__tekst">
                    <strong>{d.naam ?? "—"}</strong>
                    {regels(d, velden, ctx).map((regel) => (
                      <span key={regel.sleutel}>{regel.tekst}</span>
                    ))}
                    {velden.includes("status") && <Status dier={d} ctx={ctx} />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

/**
 * De tekstregels van een kaartje, in de volgorde die de gebruiker koos. Soort, ras en geslacht met
 * leeftijd stonden altijd al onder elkaar; velden die er los weinig toe doen — geslacht, leeftijd,
 * gecastreerd — komen samen op één regel als ze naast elkaar staan, zodat een kaartje niet uitdijt.
 */
function regels(d: PensionDier, velden: string[], ctx: Context): { sleutel: string; tekst: string }[] {
  const { t, locale } = ctx;
  const tekst = (sleutel: string): string | null => {
    switch (sleutel) {
      case "soort":
        return d.diersoort;
      case "ras":
        return d.ras;
      case "geslacht":
        return d.geslacht;
      case "leeftijd":
        return leeftijd(d.geboortedatum, ctx);
      case "geboortedatum":
        return d.geboortedatum ? datumMetJaar(d.geboortedatum, locale) : null;
      case "gecastreerd":
        return t(d.gecastreerd ? "pensionKlant.dier.gecastreerdJa" : "pensionKlant.dier.gecastreerdNee");
      case "chipnummer":
        return d.chipnummer ?? null;
      case "kenmerken":
        return d.kenmerken.length > 0 ? d.kenmerken.join(" · ") : null;
      case "bijzonderheden":
        return d.bijzonderheden;
      case "dierenarts":
        return d.dierenarts ?? null;
      case "bezoeknummer":
        return d.bezoekNummer > 0 ? t("pensionKlant.dieren.bezoek", { args: [d.bezoekNummer] }) : null;
      case "reservering": {
        const r = d.huidigeReservering;
        return r?.van ? datumMetJaar(r.van, locale) : null;
      }
      default:
        return null;
    }
  };
  // Deze drie horen bij elkaar op één regel; "Mannelijk · 17 jaar" leest als één zin.
  const samen = new Set(["geslacht", "leeftijd", "gecastreerd"]);
  const uit: { sleutel: string; tekst: string }[] = [];
  for (const sleutel of velden) {
    if (sleutel === "foto" || sleutel === "status") continue;
    const waarde = tekst(sleutel);
    if (!waarde) continue;
    const vorige = uit[uit.length - 1];
    if (vorige && samen.has(sleutel) && samen.has(vorige.sleutel)) {
      vorige.tekst = `${vorige.tekst} · ${waarde}`;
      continue;
    }
    uit.push({ sleutel, tekst: waarde });
  }
  return uit;
}

function Status({ dier, ctx }: { dier: PensionDier; ctx: Context }) {
  const { t } = ctx;
  if (dier.status === "IN_PENSION") {
    return (
      <span className="pk-dierkaart__status" data-toon="groen">
        <House size={14} strokeWidth={2.2} aria-hidden="true" />
        {t("pensionKlant.status.inPension")}
      </span>
    );
  }
  if (dier.status === "KOMENDE_RESERVERING") {
    return (
      <span className="pk-dierkaart__status" data-toon="groen">
        <VolVinkje size={14} />
        {t("pensionKlant.status.gereserveerd")}
      </span>
    );
  }
  return (
    <span className="pk-dierkaart__status">
      <Clock3 size={14} aria-hidden="true" />
      {t("pensionKlant.status.geenReservering")}
    </span>
  );
}
